import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { usePermissionGuardRedirect } from "../../hooks/usePermissionGuard";
import SkeletonTicketList from "../../loaders/SkeletonTicketList";
import PermissionsGate from "../../utils/permissionGate";
import { RenderSchool } from "../../utils/CommonGroupingItem";
import { RenderCareerDemandLevel } from "../../utils/CommonStatusItems";
import DataTableComponentBackendPagination from "../common/DataTableComponentBackendPagination";
import Button from "../common/Button/Button";
import {
    GetCareerList,
    GetCareerListFilters,
    DeleteCareer,
} from "../../services/CareerLibraryServices";
import { formatSalaryRange } from "./careerLibraryUtils";
import RowActions from "./RowActions";
import { invalidateCareerLibraryQueries } from "../../query/hooks/QueryHooksCareerLibrary";

const listParams = {};

function CareerList() {
    const history = useHistory();
    usePermissionGuardRedirect(["careerlibrarycareersview"]);
    const [reloadKey, setReloadKey] = useState(0);

    const handleDeleteCareer = async (careerId) => {
        try {
            const swalResult = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, delete it!",
            });

            if (!swalResult.isConfirmed) {
                return;
            }

            const deleteResponse = await DeleteCareer(careerId);
            if (deleteResponse.status === 200) {
                await invalidateCareerLibraryQueries();
                Swal.fire(
                    "Deleted!",
                    "The career has been deleted.",
                    "success",
                );
                setReloadKey((currentKey) => currentKey + 1);
            }
        } catch (error) {
            console.log("CareerList.js->handleDeleteCareer->Error", error);
            Swal.fire("Error", "The career could not be deleted.", "error");
        }
    };

    const columns = useMemo(
        () => [
            {
                name: "Career",
                selector: "name",
                sortField: "name",
                sortable: true,
                grow: 2,
                minWidth: "220px",
                cell: (row) => (
                    <Link
                        to={`/career-library/careers/open/${row.id}`}
                        title={row.name}
                        className="career-name-link curser feature-name"
                    >
                        <span className="textLimit100">
                            {row?.name?.trim() ? row.name : "-"}
                        </span>
                    </Link>
                ),
            },
            {
                name: "School",
                selector: "school_name",
                sortField: "school",
                sortable: true,
                grow: 2,
                minWidth: "240px",
                cell: (row) =>
                    row?.school ? (
                        <span className="feature-name">
                            <span className="textLimit100">
                                {RenderSchool(row.school).html}
                            </span>
                        </span>
                    ) : (
                        "-"
                    ),
            },
            {
                name: "Salary Range (Monthly)",
                selector: "salary_entry",
                sortField: "salary_entry",
                sortable: true,
                grow: 1,
                minWidth: "180px",
                cell: (row) => (
                    <span>
                        {formatSalaryRange(row.salary_entry, row.salary_expert)}
                    </span>
                ),
            },
            {
                name: "Demand Level",
                selector: "demand_level",
                sortField: "demand_level",
                sortable: true,
                grow: 1.5,
                minWidth: "170px",
                cell: (row) =>
                    row?.demand_level
                        ? RenderCareerDemandLevel(row.demand_level).html
                        : "-",
            },
            {
                name: "Action",
                width: "96px",
                right: true,
                cell: (row) => (
                    <RowActions
                        onOpen={() =>
                            history.push(
                                `/career-library/careers/open/${row.id}`,
                            )
                        }
                        openTitle="Open"
                        openScopes={["careerlibrarycareersedit"]}
                        onDanger={() => handleDeleteCareer(row.id)}
                        dangerTitle="Delete"
                        dangerScopes={["careerlibrarycareersdelete"]}
                    />
                ),
            },
        ],
        [history],
    );

    const filterConfig = [
        {
            key: "viaSchool",
            label: "School",
            optionsKey: "SchoolFilter",
            renderFunction: RenderSchool,
        },
        {
            key: "viaDemand",
            label: "Demand Level",
            optionsKey: "DemandFilter",
            renderFunction: RenderCareerDemandLevel,
        },
    ];

    return (
        <div
            className="tab-pane fade active show career-library-table"
            id="pills-tab11"
            role="tabpanel"
            aria-labelledby="pills-tab1"
        >
            <DataTableComponentBackendPagination
                key={reloadKey}
                columns={columns}
                getDataApi={GetCareerList}
                getFiltersApi={GetCareerListFilters}
                fileName="Career_List"
                filterConfig={filterConfig}
                showExport={true}
                storeFilters={true}
                storageKey="CareerList"
                showFilter={true}
                showSearch={true}
                defaultSortField="name"
                defaultSortAsc={true}
                additionalParams={listParams}
                LoadingComponent={<SkeletonTicketList />}
                tableButton={[
                    <Link to="/career-library/careers/open" key="create-career">
                        <PermissionsGate
                            scopes={["careerlibrarycareersadd"]}
                            errorProps={{ disabled: true }}
                        >
                            <Button
                                variant="primary"
                                iconLeft={<i className="fal fa-plus" />}
                                title="Create new"
                            >
                                Create New
                            </Button>
                        </PermissionsGate>
                    </Link>,
                ]}
            />
        </div>
    );
}

export default CareerList;
